import { nanoid } from 'nanoid'
import "imba/reset.css"
global css html ff:sans
import * as df from 'date-fns'
import type {iHistory, iHabit} from "./types"
import {icons} from './icons'

css button bgc:gray1 @hover:gray2 px:10px rd:sm 

def daysBack num, from
	from = Date.now() if from == null
	const result = []
	for i in [0...num]
		result.push df.getTime(df.subDays(from, i))
	return result

def findHabit habits, id
	for habit in habits
		return habit if habit.id === id
	return null

def habitIndex habits, id
	for habit, i in habits
		return i if habit.id === id
	return null

def buildHistory action\string, time\number, createdAt\number
	return {action, time, createdAt}

def addHabit habits\iHabit[], iconKey\iHabit
	const newHabit = {icon: iconKey, history: [], id: nanoid()}
	return [...habits, newHabit]

def completeHabit habits\iHabit[], id\string, time\number
	const index = habitIndex(habits, id)
	const newHabits = [...habits]
	newHabits[index].history.push buildHistory("complete", time, Date.now())
	return newHabits

def resetHabit habits\iHabit[], id\string, time\number
	const index = habitIndex(habits, id)
	const newHabits = [...habits]
	newHabits[index].history.push buildHistory("reset", time, Date.now())
	return newHabits

def toggleHabit habits\iHabit[], id\string, time\number
	if isCompleteForDay(habits, id, time)
		return resetHabit(habits, id, time)
	else
		return completeHabit(habits, id, time)

def isCompleteForDay habits\array, id\string, time\number
	const habit = findHabit(habits, id)
	const todayOnly = habit.history.filter do(e) df.isSameDay(e.time, time)
	const sortedHistory = todayOnly.sort do(a, b) b.createdAt - a.createdAt
	const mostRecent = sortedHistory[0]
	return mostRecent != null and mostRecent.action === "complete" and df.isSameDay(mostRecent.time, time)

def saveInStorage key\string, data\iHabit[]
	global.localStorage.setItem(key, JSON.stringify(data))

def loadFromStorage key\string
	const raw = global.localStorage.getItem(key)
	return null if raw == null or raw.trim() === ""
	const parsed = JSON.parse(raw)
	return null if parsed == null
	return parsed

def getCannonicalDateForDay day
	df.startOfDay(day).getTime()
	
def getDaysOfYear
	const result = []
	for i in [0...365]
		result.push getCannonicalDateForDay(df.addDays(df.startOfYear(Date.now()), i).getTime())
	return result

class Habits
	prop data\Habit[] = []

	static prop localStorageKey = 'resolution-matrix-data'

	constructor
		data = load() ?? []

	def persist
		saveInStorage(Habits.localStorageKey, data)
	
	def load
		loadFromStorage(Habits.localStorageKey)
	
	get all
		data

	def add iconKey\string	
		data = addHabit(data, iconKey)	

	def complete id\string, date\number
		data = completeHabit(data, id, date)
	
	def reset id\string, date\number
		data = resetHabit(data, id, date)

	def toggle id\string, date\number
		data = toggleHabit(data, id, date)
	
	def isCompleteForDay day\number, id\string
		isCompleteForDay(data, id, day)


tag app
	prop habits = new Habits()

	prop daysOfYear = getDaysOfYear()

	css d:flex jc:center
		.container d:vflex w:100%
		.row d:flex g:6px p:3px ai:center pos:relative w:100% mx:auto jc:center
		.row.current bgc:blue1
		.date fs:10px w:150px c:gray4 ta:right ff:mono 
		.row
			svg size:36px
			svg >>> fill:cool4
			svg rd:full p:3px
			svg.checked bgc:green6
			svg.checked >>> fill:white
	
	def rendered
		unless rendered?
			const scrollToDay = getCannonicalDateForDay(df.subDays(Date.now(), 7))
			const element = self.querySelector("#day-{scrollToDay}")
			if element != null
				setTimeout(&, 0) do
					element.scrollIntoView({behavior: 'smooth'})

	def handleHabitClick id\string, day\number
		habits.toggle(id, day)
		habits.persist()

	def handleAddHabitClick iconKey\string
		habits.add(iconKey)
		habits.persist()
	
	css .icons size:500px bgc:yellow1 d:grid gtc:repeat(auto-fit, minmax(50px, 1fr)) p:$small-space grid-gap:5px
		.button d:block pos:relative rd:$small-radius
			svg >>> path fill:cooler6
		.button@hover bgc:cooler1
			svg >>> path fill:cooler9
		.button .svg-wrap inset:10px
		.button .svg-wrap svg size:100%
		.button@before content:"" aspect-ratio:1 d:block 

	prop showIcons = false

	<self>
		# <div.debug [pos:absolute ff:mono p:5px t:0 l:0 bgc:yellow0 fs:10px ws:pre]> JSON.stringify habits, null, 2

		<button @click=(showIcons = !showIcons) [pos:absolute t:5px l:5px zi:10]> "Add"
		if showIcons
			<div.icons [pos:absolute t:35px l:5px size:300px zi:10 of:scroll rd:md]>
				for own key, icon of icons
					<svg src=icon.svg @click=(do handleAddHabitClick(key))>

		<div.container>
			for day in daysOfYear
				<div.row .current=df.isSameDay(day, Date.now()) id="day-{day}">
					<div.date> df.format day, "EEE, LLL dd"
					for habit in habits.all
						<svg src=icons[habit.icon]..svg
							.checked=habits.isCompleteForDay(day, habit.id)
							@click=(do handleHabitClick(habit.id, day))
						>
imba.mount <app>